import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TablePagination,
} from '@mui/material';
import TenantService from './services/tenantService.ts';
import Loader from '../../../common/components/Loader.tsx';
import { useGlobalStore } from '../../../application/store/store.tsx';
import { Tenant } from '../../../application/types/tenants.ts';
import { DateTime } from 'luxon';
import { NavigationRoutes } from '../../../application/router/routes.ts';
import { Add, WarningOutlined } from '@mui/icons-material';
import EmptyTablePlaceholder from '../../../common/components/EmptyTablePlaceholder.tsx';

const TenantsOverview = () => {
    const tenantService = new TenantService();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { setMessage } = useGlobalStore((state) => state);
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const response = await tenantService.getTenants();
                setTenants(response);
            } catch (error) {
                setMessage('Kunde inte hämta organisationer!', 'error', 6000);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTenants();
    }, []);

    if (isLoading) {
        return <Loader />;
    }

    const isLicenseExpiringSoon = (expiryDate: string) => {
        const now = DateTime.now();
        const expiry = DateTime.fromISO(expiryDate);
        return expiry.diff(now, 'months').months < 2;
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedTenants = tenants.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Paper className="p-4 sm:p-6 rounded-xl shadow-md">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                <Typography variant="h5" fontWeight="bold">
                    Organisationer
                </Typography>
                <Button
                    startIcon={<Add />}
                    variant="contained"
                    color="primary"
                    onClick={() => navigate(NavigationRoutes.createTenant)}
                    className="self-start sm:self-auto"
                >
                    Ny organisation
                </Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-md border border-gray-200">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow className="bg-gray-100">
                                <TableCell>Organisation ID</TableCell>
                                <TableCell>Namn</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Licens t.o.m.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedTenants.length === 0 ? (
                                <EmptyTablePlaceholder
                                    colSpan={4}
                                    title="Inga organisationer att visa"
                                    subtitle="Justera filter eller klicka på ‘Ny organisation’ för att skapa en ny."
                                />
                            ) : (
                                paginatedTenants.map((tenant: Tenant) => (
                                    <TableRow
                                        key={tenant.id}
                                        hover
                                        onClick={() =>
                                            navigate(
                                                `${NavigationRoutes.tenantView}/${tenant.id}`
                                            )
                                        }
                                        className="transition hover:bg-gray-50 cursor-pointer"
                                    >
                                        <TableCell className="whitespace-nowrap">
                                            {tenant.id}
                                        </TableCell>
                                        <TableCell className="min-w-[150px]">
                                            {tenant.name}
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                                    tenant.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-200 text-gray-700'
                                                }`}
                                            >
                                                {tenant.isActive
                                                    ? 'Aktiv'
                                                    : 'Inaktiv'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {DateTime.fromISO(
                                                    tenant.validUpto instanceof
                                                        Date
                                                        ? tenant.validUpto.toISOString()
                                                        : tenant.validUpto
                                                ).toFormat('yyyy-MM-dd HH:mm')}
                                                {isLicenseExpiringSoon(
                                                    tenant.validUpto instanceof
                                                        Date
                                                        ? tenant.validUpto.toISOString()
                                                        : tenant.validUpto
                                                ) && (
                                                    <WarningOutlined
                                                        color="warning"
                                                        fontSize="small"
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Pagination */}
            <div className="mt-4">
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={tenants.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Antal poster:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                    }
                />
            </div>
        </Paper>
    );
};

export default TenantsOverview;
